import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Loader2, ArrowRight, ShieldCheck, AlertTriangle, FileEdit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import api, { vendorsAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const GrnSummary = ({ data }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="grn-correction-summary">
    <div><p className="text-gray-500 text-xs">GRN</p><p className="font-semibold">{data.grn.grn_number}</p><p className="text-xs text-gray-500 capitalize">{data.grn_type} · {data.items_count} items</p></div>
    <div><p className="text-gray-500 text-xs">Current vendor</p><p className="font-semibold" data-testid="grn-current-vendor">{data.vendor?.name || data.grn.vendor_snapshot?.vendor_name}</p><p className="text-xs text-gray-500">Balance {inr(data.vendor?.current_balance)}</p></div>
    <div><p className="text-gray-500 text-xs">Amount</p><p className="font-semibold">{inr(data.grn.grand_total)}</p><p className="text-xs text-gray-500">Inv {data.grn.invoice_number || '—'} · {data.grn.invoice_date ? formatDate(data.grn.invoice_date) : '—'}</p></div>
    <div><p className="text-gray-500 text-xs">Completed</p><p className="font-semibold">{data.completed_on ? formatDate(data.completed_on) : '—'}</p><p className="text-xs text-gray-500">{data.age_days} days ago · {data.grn.warehouse_name}{data.po?.po_number ? ` · PO ${data.po.po_number}` : ''}</p></div>
  </div>
);

export const GRNCorrectionTab = () => {
  const { toast } = useToast();
  const [grnNumber, setGrnNumber] = useState('');
  const [looking, setLooking] = useState(false);
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [newVendorId, setNewVendorId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get('/hq/grn-corrections/history').then(r => setHistory(r.data)).catch(() => {});
  useEffect(() => {
    vendorsAPI.list({ limit: 500 }).then(r => setVendors(r.data?.items || r.data || [])).catch(() => {});
    loadHistory();
  }, []);

  const lookup = async () => {
    if (!grnNumber.trim()) return;
    setLooking(true); setData(null); setNewVendorId('');
    try {
      const r = await api.get('/hq/grn-corrections/lookup', { params: { grn_number: grnNumber.trim() } });
      setData(r.data);
      setInvoiceNumber(r.data.grn.invoice_number || '');
      setInvoiceDate(r.data.grn.invoice_date || '');
    } catch (e) {
      toast({ title: 'Not found', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLooking(false); }
  };

  const reset = () => { setData(null); setGrnNumber(''); setNewVendorId(''); setReason(''); setCode(''); loadHistory(); };

  const submit = async (mode) => {
    setSubmitting(true);
    try {
      const base = { grn_id: data.grn.id, reason, security_code: code };
      const invoiceChanged = invoiceNumber !== (data.grn.invoice_number || '') || invoiceDate !== (data.grn.invoice_date || '');
      let r;
      if (mode === 'vendor') {
        r = await api.post('/hq/grn-corrections/change-vendor', { ...base, new_vendor_id: newVendorId, ...(invoiceChanged ? { invoice_number: invoiceNumber, invoice_date: invoiceDate || null } : {}) });
        toast({ title: `Vendor corrected on ${r.data.grn_number}`, description: `${r.data.old_vendor.name} now ${inr(r.data.old_vendor.balance)} · ${r.data.new_vendor.name} now ${inr(r.data.new_vendor.balance)}` });
      } else {
        r = await api.post('/hq/grn-corrections/update-invoice', { ...base, invoice_number: invoiceNumber, invoice_date: invoiceDate || null });
        toast({ title: `Invoice updated on ${r.data.grn_number}` });
      }
      reset();
    } catch (e) {
      toast({ title: 'Correction failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  const invoiceChanged = data && (invoiceNumber !== (data.grn.invoice_number || '') || invoiceDate !== (data.grn.invoice_date || ''));
  const canSubmitVendor = data?.eligible && newVendorId && newVendorId !== data.grn.vendor_id && reason.trim().length >= 5 && code.trim();
  const canSubmitInvoice = data?.invoice_edit_eligible && invoiceChanged && reason.trim().length >= 5 && code.trim();

  return (
    <div className="space-y-4" data-testid="grn-correction-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><FileEdit className="w-5 h-5 text-amber-600" /> GRN Correction</CardTitle>
          <CardDescription>Change the vendor on a completed GRN (both vendor ledgers are corrected with back-dated entries) or fix its invoice number/date. Allowed within 90 days of completion, only when no payment is recorded against the GRN. Needs the HQ security code.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2 max-w-md">
            <Input placeholder="GRN number, e.g. GRN-L-2026-00012" value={grnNumber} onChange={(e) => setGrnNumber(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} data-testid="grn-lookup-input" />
            <Button onClick={lookup} disabled={looking} data-testid="grn-lookup-btn">{looking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
          </div>

          {data && (
            <div className="space-y-4 border-t pt-4">
              <GrnSummary data={data} />
              {data.blockers.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 space-y-1" data-testid="grn-blockers">
                  {data.blockers.map((b, i) => <p key={i} className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{b}</p>)}
                </div>
              )}
              {data.grn.correction_history?.length > 0 && (
                <div className="text-xs text-gray-500">Previously corrected {data.grn.correction_history.length}×, last by {data.grn.correction_history.at(-1).by_name} on {formatDate(data.grn.correction_history.at(-1).at)}</div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Correct vendor</Label>
                  <Select value={newVendorId} onValueChange={setNewVendorId} disabled={!data.eligible}>
                    <SelectTrigger data-testid="grn-new-vendor-select"><SelectValue placeholder="Select the correct vendor" /></SelectTrigger>
                    <SelectContent>
                      {vendors.filter(v => v.id !== data.grn.vendor_id).map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}{v.vendor_code ? ` (${v.vendor_code})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newVendorId && (
                    <p className="text-xs text-gray-600 flex items-center gap-1" data-testid="grn-vendor-preview">
                      {data.vendor?.name} <Badge variant="outline" className="text-red-600 border-red-200">− {inr(data.grn.grand_total)}</Badge>
                      <ArrowRight className="w-3 h-3" />
                      {vendors.find(v => v.id === newVendorId)?.name} <Badge variant="outline" className="text-green-700 border-green-200">+ {inr(data.grn.grand_total)}</Badge>
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2"><Label>Invoice number</Label><Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} disabled={!data.invoice_edit_eligible} data-testid="grn-invoice-number" /></div>
                  <div className="space-y-2"><Label>Invoice date</Label><Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} disabled={!data.invoice_edit_eligible} data-testid="grn-invoice-date" /></div>
                </div>
              </div>

              <div className="grid md:grid-cols-[1fr_220px] gap-4">
                <div className="space-y-2"><Label>Reason (min 5 chars)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this correction needed?" data-testid="grn-reason" /></div>
                <div className="space-y-2"><Label className="flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Security code</Label><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="HQ security code" data-testid="grn-security-code" /></div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={reset}>Cancel</Button>
                <Button variant="outline" disabled={!canSubmitInvoice || submitting || !!newVendorId} onClick={() => submit('invoice')} data-testid="grn-update-invoice-btn">Update invoice only</Button>
                <Button className="bg-amber-600 hover:bg-amber-700" disabled={!canSubmitVendor || submitting} onClick={() => submit('vendor')} data-testid="grn-change-vendor-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Change vendor{invoiceChanged ? ' + invoice' : ''}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Correction history</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? <p className="text-sm text-gray-400">No corrections yet.</p> : (
            <Table data-testid="grn-correction-history">
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>GRN</TableHead><TableHead>Type</TableHead><TableHead>Change</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>By</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
              <TableBody>
                {history.map(h => (
                  <TableRow key={h.id}>
                    <TableCell className="whitespace-nowrap text-xs">{formatDate(h.at)}</TableCell>
                    <TableCell className="font-mono text-xs">{h.grn_number}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{h.type.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-xs">{h.type === 'vendor_change' ? `${h.old_vendor_name} → ${h.new_vendor_name}` : `Inv ${h.old_invoice_number || '—'} → ${h.new_invoice_number || '—'}${h.old_invoice_date !== h.new_invoice_date ? ` · ${h.old_invoice_date || '—'} → ${h.new_invoice_date || '—'}` : ''}`}</TableCell>
                    <TableCell className="text-right">{inr(h.amount)}</TableCell>
                    <TableCell className="text-xs">{h.by_name}</TableCell>
                    <TableCell className="text-xs text-gray-500 max-w-xs truncate" title={h.reason}>{h.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
